import React from 'react';
import {
    Box,
    IconButton,
    MenuItem,
    Pagination,
    PaginationItem,
    Select,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FirstPage, LastPage, ExpandMore } from '@mui/icons-material';
import { OBSIDIAN, OBSIDIAN_BODY_FONT, OBSIDIAN_DISPLAY_FONT } from '../../../constants/obsidianDashboardTheme';
import { obsidianFilterMenuProps } from './obsidianTableTheme';

export interface ObsidianDashboardTablePaginationProps {
    page: number;
    rowsPerPage: number;
    totalCount: number;
    rowsPerPageOptions: number[];
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rows: number) => void;
}

export function ObsidianDashboardTablePagination({
    page,
    rowsPerPage,
    totalCount,
    rowsPerPageOptions,
    onPageChange,
    onRowsPerPageChange,
}: ObsidianDashboardTablePaginationProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const pageCount = Math.max(1, Math.ceil(totalCount / rowsPerPage) || 1);
    const safePage = Math.min(Math.max(1, page), pageCount);
    const rangeStart = totalCount === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
    const rangeEnd = Math.min(safePage * rowsPerPage, totalCount);

    const pageButtonSx = {
        minWidth: { xs: 32, sm: 36, md: 40 },
        height: { xs: 32, sm: 36, md: 40 },
        fontSize: { xs: 12, md: 14 },
        borderRadius: 999,
        flexShrink: 0,
    };

    const renderPageItem = (item: React.ComponentProps<typeof PaginationItem>) => (
        <PaginationItem
            {...item}
            sx={{
                ...(item.selected
                    ? {
                          ...pageButtonSx,
                          bgcolor: `${OBSIDIAN.primary}1a`,
                          color: OBSIDIAN.primary,
                          fontFamily: OBSIDIAN_DISPLAY_FONT,
                          fontWeight: 800,
                          border: `2px solid ${OBSIDIAN.primary}66`,
                      }
                    : {
                          ...pageButtonSx,
                          color: OBSIDIAN.onSurfaceVariant,
                          fontFamily: OBSIDIAN_DISPLAY_FONT,
                          fontWeight: 700,
                          '&:hover': { bgcolor: OBSIDIAN.surfaceContainer },
                      }),
            }}
        />
    );

    const navPillSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        bgcolor: OBSIDIAN.surfaceContainerLow,
        p: 0.75,
        borderRadius: 999,
        maxWidth: '100%',
        flexWrap: 'nowrap' as const,
        ...(isMobile
            ? { width: '100%', justifyContent: 'center' }
            : { alignSelf: 'flex-end', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }),
    };

    return (
        <Box
            sx={{
                mt: { xs: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: { xs: 2, md: 3 },
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
            }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    gap: { xs: 1.25, sm: 2 },
                    flexWrap: 'wrap',
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.25, sm: 2 }, flexWrap: 'wrap' }}>
                    <Typography
                        sx={{
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontSize: { xs: 10, sm: 12 },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.12em',
                            color: OBSIDIAN.onSurfaceVariant,
                            whiteSpace: 'nowrap',
                        }}>
                        Rows per page:
                    </Typography>
                    <Select
                        size="small"
                        value={rowsPerPage}
                        onChange={e => onRowsPerPageChange(Number(e.target.value))}
                        IconComponent={ExpandMore}
                        MenuProps={obsidianFilterMenuProps}
                        sx={{
                            minWidth: { xs: 64, sm: 72 },
                            bgcolor: OBSIDIAN.surfaceContainer,
                            borderRadius: 2,
                            fontFamily: OBSIDIAN_BODY_FONT,
                            fontWeight: 700,
                            fontSize: { xs: 13, sm: 14 },
                            color: OBSIDIAN.primary,
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-icon': { color: OBSIDIAN.primary },
                        }}>
                        {rowsPerPageOptions.map(n => (
                            <MenuItem key={n} value={n}>
                                {n}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Typography
                    sx={{
                        fontFamily: OBSIDIAN_BODY_FONT,
                        fontSize: { xs: 10, sm: 12 },
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        color: OBSIDIAN.onSurfaceVariant,
                        whiteSpace: 'nowrap',
                    }}>
                    {rangeStart}-{rangeEnd} of {totalCount}
                </Typography>
            </Box>

            <Box sx={navPillSx}>
                {!isMobile && (
                    <IconButton
                        size="small"
                        aria-label="First page"
                        disabled={safePage <= 1}
                        onClick={() => onPageChange(1)}
                        sx={{ ...paginationIconSx, flexShrink: 0 }}>
                        <FirstPage fontSize="small" />
                    </IconButton>
                )}
                <IconButton
                    size="small"
                    aria-label="Previous page"
                    disabled={safePage <= 1}
                    onClick={() => onPageChange(safePage - 1)}
                    sx={{ ...paginationIconSx, flexShrink: 0 }}>
                    <ChevronLeft fontSize="small" />
                </IconButton>
                <Pagination
                    count={pageCount}
                    page={safePage}
                    onChange={(_, p) => onPageChange(p)}
                    siblingCount={1}
                    boundaryCount={isMobile ? 0 : 1}
                    hidePrevButton
                    hideNextButton
                    renderItem={renderPageItem}
                    sx={{
                        flexShrink: 0,
                        '& .MuiPagination-ul': { gap: 0.25, flexWrap: 'nowrap' },
                    }}
                />
                <IconButton
                    size="small"
                    aria-label="Next page"
                    disabled={safePage >= pageCount}
                    onClick={() => onPageChange(safePage + 1)}
                    sx={{ ...paginationIconSx, flexShrink: 0 }}>
                    <ChevronRight fontSize="small" />
                </IconButton>
                {!isMobile && (
                    <IconButton
                        size="small"
                        aria-label="Last page"
                        disabled={safePage >= pageCount}
                        onClick={() => onPageChange(pageCount)}
                        sx={{ ...paginationIconSx, flexShrink: 0 }}>
                        <LastPage fontSize="small" />
                    </IconButton>
                )}
            </Box>
        </Box>
    );
}

const paginationIconSx = {
    color: OBSIDIAN.onSurfaceVariant,
    borderRadius: 999,
    '&:hover': { bgcolor: OBSIDIAN.surfaceContainer, color: OBSIDIAN.onSurface },
    '&.Mui-disabled': { opacity: 0.3 },
};
